import {
  Checkbox,
  Link,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
} from '@mui/material';
import { Regex } from '@utils/constants';
import React, { FC, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import Button from '../Button/Button';
import Pagination from '../Pagination/Pagination';
import { RowNoData } from '../styled';
import './Table.scss';

type RoleType = 'owner' | 'coOwner' | 'system' | 'view' | 'search';

interface AppTableProps {
  uncheckable?: boolean;
  header?: ITableHeader[];
  data?: ITableData[];
  actionColumns?: string;
  paginationMeta?: ListMeta;
  role?: RoleType;
  isUnderRole?: (
    row: ITableData,
    uncheckable: boolean,
    role?: RoleType,
  ) => boolean;
  onClickCell?: (row: ITableData, column: string) => void;
  onChangePage?: (page: string) => void;
  onDelete?: (ids: number[]) => void;
}

const AppTable: FC<AppTableProps> = ({
  uncheckable = false,
  header = [],
  data = [],
  actionColumns,
  paginationMeta,
  role,
  isUnderRole,
  onClickCell,
  onChangePage,
  onDelete,
}) => {
  const { t } = useTranslation();
  const isEnableClickRow = actionColumns?.includes('all');
  const [checkedIds, setCheckedIds] = useState<number[]>([]);

  useEffect(() => {
    setCheckedIds([]);
  }, [data]);

  const handleCheckAll = (_: React.ChangeEvent, checked: boolean) => {
    if (checked) {
      setCheckedIds(data.map(({ id }) => id));
    } else {
      setCheckedIds([]);
    }
  };

  const handleCheck = (event: React.ChangeEvent, checked: boolean) => {
    const id = Number(event.target.id);
    if (checked) {
      setCheckedIds(checkedIds.concat(id));
    } else {
      setCheckedIds(checkedIds.filter((cId) => cId !== id));
    }
  };

  const handleClickRow = (row: ITableData) => {
    if (isEnableClickRow && onClickCell) onClickCell(row, 'all');
  };

  return (
    <div className="AppTable">
      <TableContainer>
        <Table>
          {header.length && (
            <TableHead>
              <TableRow>
                {!uncheckable && (
                  <TableCell>
                    <Checkbox
                      disabled={!!role || !data.length}
                      size="large"
                      checked={
                        !!checkedIds.length && checkedIds.length === data.length
                      }
                      indeterminate={
                        !!checkedIds.length && checkedIds.length < data.length
                      }
                      onChange={handleCheckAll}
                    />
                  </TableCell>
                )}
                {header.map(({ label, width }) => (
                  <TableCell key={label} width={width + '%'}>
                    {label && t(label)}
                  </TableCell>
                ))}
              </TableRow>
            </TableHead>
          )}
          <TableBody>
            {data.map((row) => (
              <TableRow
                key={row.id}
                onClick={() => handleClickRow(row)}
                hover={isEnableClickRow}
              >
                {!uncheckable && (
                  <TableCell>
                    <Checkbox
                      id={String(row.id)}
                      size="large"
                      checked={checkedIds.includes(row.id)}
                      onChange={handleCheck}
                      disabled={
                        isUnderRole && isUnderRole(row, uncheckable, role)
                      }
                    />
                  </TableCell>
                )}
                {header.map(({ column, translate, renderActions }) => {
                  const rowText = row[column];

                  const isLink = String(rowText).match(Regex.RULE.LINK);
                  return (
                    <TableCell key={column} data-type={column}>
                      {renderActions ? (
                        renderActions(row, onClickCell)
                      ) : actionColumns?.split(',')?.includes(column) ? (
                        isUnderRole && isUnderRole(row, uncheckable, role) ? (
                          translate ? (
                            t(rowText)
                          ) : (
                            rowText
                          )
                        ) : (
                          <Link
                            target="_blank"
                            underline={isLink ? 'hover' : 'none'}
                            component={isLink ? 'a' : 'button'}
                            href={rowText}
                            onClick={() => onClickCell?.(row, column)}
                          >
                            {translate ? t(rowText) : rowText}
                          </Link>
                        )
                      ) : translate ? (
                        t(rowText)
                      ) : (
                        rowText
                      )}
                    </TableCell>
                  );
                })}
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
      {data.length === 0 && <RowNoData>{t('message.MSI00001')}</RowNoData>}
      <div className="AppTable__footer">
        {!uncheckable && (
          <Button
            color="error"
            disabled={!checkedIds.length}
            title="common.button_delete"
            onClick={() => onDelete?.(checkedIds)}
          />
        )}
        <Pagination meta={paginationMeta} onChange={onChangePage} />
      </div>
    </div>
  );
};

export default AppTable;
